import { Dropdown, Input, Flex, TooltipTrigger, Icon, TooltipContent, ErrorCard, Tooltip } from "@repo/ping-design";
import { useState, useCallback } from "react";
import { PermitsDetailModal } from "./PermitsDetailModal";
import {
  AOA_SECTION_MAP,
  AOA_SEVERITY_COLORS_LOOKUP,
  BuildingPanelField,
  CollapsibleSection,
  DISABLED_ENRICH_BUTTON_SOURCES,
  ERROR_DISABLED_SECTIONS,
  formatSectionTooltip,
  INITIAL_AOA_SECTIONS_OPEN,
  INTERNATIONAL_LOCATION_ERROR_SOURCES,
  MISSING_VALUE,
  parseCurrency,
  useAnalytics,
  POSTHOG_EVENTS,
  APPEND_ADDITIONAL_SOURCES,
  SOURCES,
} from "@repo/ping-react-js";
import cx from "classnames";
import "./AoASection.scss";
import AoaButton from "../AoaButton";

const getInitialFields = (options: any[], policyBuildingData: any) => {
  return options.reduce((acc: any, opt: any) => {
    const defaultValue = opt.defaultValue || "";
    return {
      ...acc,
      [opt.fieldName]: policyBuildingData?.[opt.fieldName] || defaultValue,
    };
  }, {});
};

const getInitialSectionsState = (
  options: any[],
  policyBuildingData: any,
  integrationSettings?: Record<string, any>,
) => {
  if (!options || options.length === 0) return {};

  return options.reduce((acc: any, option: any) => {
    const sectionIds = [option.id, ...APPEND_ADDITIONAL_SOURCES(option.id, integrationSettings)].filter(
      (sectionId, index, sectionIdList) => sectionIdList.indexOf(sectionId) === index,
    );

    sectionIds.forEach((sectionId) => {
      if (acc[sectionId]) return;

      const initialFields = getInitialFields(AOA_SECTION_MAP[sectionId]?.aoaFields || [], policyBuildingData);

      acc[sectionId] = {
        editing: false,
        fields: initialFields,
        debouncedFields: initialFields,
      };
    });

    return acc;
  }, {});
};

type AoASectionProps = {
  sovid: string | null;
  options: any[];
  aoaIntegrations: any;
  policyBuildingData: any;
  requiredFields?: Record<string, any>;
  aoaInputOptions?: Record<string, Record<string, Array<{ label: string; value: string | number }>>>;
  buildingId: string | null;
  buttonRefs: React.MutableRefObject<Record<string, HTMLButtonElement | HTMLAnchorElement | null>>;
  isReadOnly?: boolean;
  isInternationalLocationError?: boolean;
  onResetAoASection?: (sectionId: string) => void;
  onClearAoASection?: () => void;
  onSetAoAResponse?: (data: any) => void;
  triggerAoAQuery: (args: any) => Promise<any>;
  isSourceFetching: (source: string) => boolean;
  shouldBlurField?: (sectionId: string, field: BuildingPanelField) => boolean;
  integrationSettings?: Record<string, any>;
};

export const AoASection = ({
  sovid,
  options,
  aoaIntegrations,
  policyBuildingData,
  requiredFields,
  aoaInputOptions,
  buildingId,
  buttonRefs,
  isReadOnly,
  isInternationalLocationError = false,
  onResetAoASection,
  onSetAoAResponse,
  triggerAoAQuery,
  isSourceFetching,
  shouldBlurField,
  integrationSettings,
}: AoASectionProps) => {
  const [error, setError] = useState<{
    source: string;
    message: string;
  } | null>(null);

  const { captureEvent } = useAnalytics();

  const [sectionsState, setSectionsState] = useState(() => {
    return getInitialSectionsState(options, policyBuildingData, integrationSettings);
  });

  const [jsonModalData, setJsonModalData] = useState<any>(null);

  const getSectionDisabledReason = useCallback(
    (sectionId: string): string | null => {
      const sectionState = sectionsState[sectionId];
      if (!sectionState) return "Section not initialized";

      const aoaFields = AOA_SECTION_MAP[sectionId]?.aoaFields || [];

      const missingAoaInputs =
        aoaFields.length > 0
          ? aoaFields
              .filter((field) => field.isRequired && !sectionState.debouncedFields[field.fieldName])
              .map((field) => field.label || field.fieldName)
          : [];
      if (missingAoaInputs.length > 0) {
        return `Required input(s) missing: ${missingAoaInputs.join(", ")}`;
      }

      const sourcesToCheck = APPEND_ADDITIONAL_SOURCES(sectionId, integrationSettings);
      let missingRequiredFields: string[] = [];
      sourcesToCheck.forEach((src) => {
        const requiredForSrc = requiredFields?.[src];
        if (Array.isArray(requiredForSrc) && requiredForSrc.length > 0) {
          requiredForSrc.forEach((field) => {
            if (policyBuildingData?.[field] == null || policyBuildingData?.[field] === "") {
              missingRequiredFields.push(field);
            }
          });
        }
      });

      const hasNoAoaFieldConfig = !aoaFields || aoaFields.length === 0;
      if (hasNoAoaFieldConfig && missingRequiredFields.length > 0) {
        return `Missing required field(s): ${missingRequiredFields.join(", ")}`;
      }

      return null;
    },
    [sectionsState, requiredFields, policyBuildingData, integrationSettings],
  );

  const handleSelectChange = useCallback((sectionId: string, field: string, value: any) => {
    setSectionsState((prev: any) => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        fields: {
          ...prev[sectionId].fields,
          [field]: value,
        },
        debouncedFields: {
          ...prev[sectionId].debouncedFields,
          [field]: value,
        },
      },
    }));
  }, []);

  const handleInputChange = useCallback((sectionId: string, field: string, value: any) => {
    setSectionsState((prev: any) => {
      const currentState = prev[sectionId];
      if (!currentState) return prev;

      return {
        ...prev,
        [sectionId]: {
          ...currentState,
          fields: {
            ...currentState.fields,
            [field]: value,
          },
          debouncedTimeoutId: setTimeout(() => {
            setSectionsState((latest: any) => ({
              ...latest,
              [sectionId]: {
                ...latest[sectionId],
                debouncedFields: {
                  ...latest[sectionId].debouncedFields,
                  [field]: value,
                },
                debouncedTimeoutId: null,
              },
            }));
          }, 300),
        },
      };
    });

    return () => {
      setSectionsState((prev: any) => {
        const timeoutId = prev[sectionId]?.debouncedTimeoutId;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        return prev;
      });
    };
  }, []);

  const toggleEditing = useCallback((sectionId: string) => {
    setSectionsState((prev: any) => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        editing: !prev[sectionId].editing,
      },
    }));
  }, []);

  const getFieldsToShow = useCallback(
    (sectionId: string) => {
      const aoaFields = AOA_SECTION_MAP[sectionId]?.aoaFields || [];
      const sectionInputOptions = aoaInputOptions?.[sectionId] || {};

      return aoaFields
        .filter((field) => {
          return policyBuildingData?.[field.fieldName] ? field.isAlwaysShow : true;
        })
        .map((field) => {
          const defaultInputOptions =
            field.fieldName === "const__bldg_year_built" ? { date: true, datePattern: ["Y"] } : { numeral: true };
          const fieldOptionsConfig = "options" in field ? field.options : undefined;

          return {
            name: field.fieldName,
            label: field.label,
            placeholder: field.placeholder,
            inputType: field.inputType,
            required: field.isRequired,
            fieldOptions: {
              selectOptions: sectionInputOptions[field.fieldName] || fieldOptionsConfig?.selectOptions,
              inputOptions:
                fieldOptionsConfig && "inputOptions" in fieldOptionsConfig
                  ? fieldOptionsConfig.inputOptions
                  : defaultInputOptions,
            },
          };
        });
    },
    [aoaInputOptions, policyBuildingData],
  );

  const buildRequestData = useCallback(
    (sectionId: string) => {
      const data = {
        ...policyBuildingData,
        ...sectionsState[sectionId]?.debouncedFields,
      };
      if (data.const__num_stories && isNaN(Number(data.const__num_stories))) {
        delete data.const__num_stories;
      }
      return data;
    },
    [policyBuildingData, sectionsState],
  );

  const renderField = useCallback(
    (sectionId: string, field: any) => {
      const { name, label, placeholder, inputType, fieldOptions, required } = field;
      const sectionState = sectionsState[sectionId];

      if (!sectionState) return null;

      const isE2ValueField = sectionId === SOURCES.E2VALUE;
      const fieldClassName = cx("AoASection__FieldsContainer__Field", {
        "AoASection__FieldsContainer__Field--E2Value": isE2ValueField,
      });

      return (
        <div className={fieldClassName} key={`${sectionId}-${name}-field`}>
          {inputType === "select" ? (
            <Dropdown
              key={`${name}-select`}
              selected={sectionState.fields[name]}
              label={label}
              options={fieldOptions.selectOptions || []}
              onChange={(selection: any) => {
                handleSelectChange(sectionId, name, selection.value);
              }}
              size="md"
              required={required}
              triggerLabelClasses={cx({
                "AoASection__DropdownTriggerLabel--truncate": isE2ValueField,
              })}
              menuItemClasses={cx({
                "AoASection__DropdownMenuItem--wrap": isE2ValueField,
              })}
            />
          ) : (
            <Input
              key={`${name}-input`}
              name={name}
              label={label}
              placeholder={placeholder}
              options={fieldOptions.inputOptions || { numeral: true }}
              value={sectionState.fields[name]}
              onChange={(field: string, value: any) => {
                const cleanupFn = handleInputChange(sectionId, field, parseCurrency(value));
                return cleanupFn;
              }}
              required={required}
            />
          )}
        </div>
      );
    },
    [sectionsState, handleSelectChange, handleInputChange],
  );

  const renderTableField = useCallback(
    (sectionId: string, field: BuildingPanelField, key: string) => {
      const isBlurred = shouldBlurField?.(sectionId, field);
      const fieldStyle = {
        color: AOA_SEVERITY_COLORS_LOOKUP[isBlurred ? "DEFAULT" : field?.c || "DEFAULT"],
      };
      const trimmedLabel = field.k?.trim?.() || field.k;
      const lastSpaceIndex = trimmedLabel.lastIndexOf(" ");
      const labelPrefix = lastSpaceIndex === -1 ? "" : trimmedLabel.slice(0, lastSpaceIndex + 1);
      const labelSuffix = lastSpaceIndex === -1 ? trimmedLabel : trimmedLabel.slice(lastSpaceIndex + 1);

      return (
        <Flex key={key} direction="row" gap="1" align="center" className="AoASection__ValueSection__Data__Maps">
          {field?.t ? (
            <Tooltip placement="bottom">
              <TooltipTrigger>
                <span className="AoASection__ValueSection__Data__Maps__Label" style={fieldStyle}>
                  <span className="AoASection__ValueSection__Data__Maps__LabelContent">
                    {labelPrefix}
                    <span className="AoASection__ValueSection__Data__Maps__LabelTail">
                      {labelSuffix}
                      <span className="AoASection__ValueSection__Data__Maps__LabelIcon">
                        <Icon iconName="Info" size={14} />
                      </span>
                    </span>
                  </span>
                </span>
              </TooltipTrigger>
              <TooltipContent>{field.t}</TooltipContent>
            </Tooltip>
          ) : (
            <span className="AoASection__ValueSection__Data__Maps__Label" style={fieldStyle}>
              {field.k}
            </span>
          )}
          {field?.vt && !isBlurred ? (
            <Tooltip>
              <TooltipTrigger>
                <span
                  aria-label={isBlurred ? "Available on a paid plan" : undefined}
                  className={cx("AoASection__ValueSection__Data__Maps__Value", {
                    "AoASection__ValueSection__Data__Maps__Value--Blurred": isBlurred,
                  })}
                  style={fieldStyle}
                >
                  {isBlurred ? "123456" : field.v || MISSING_VALUE}
                </span>
              </TooltipTrigger>
              <TooltipContent>{field.vt}</TooltipContent>
            </Tooltip>
          ) : (
            <span
              aria-label={isBlurred ? "Available on a paid plan" : undefined}
              className={cx("AoASection__ValueSection__Data__Maps__Value", {
                "AoASection__ValueSection__Data__Maps__Value--Blurred": isBlurred,
              })}
              style={fieldStyle}
            >
              {isBlurred ? "123456" : field.v || MISSING_VALUE}
            </span>
          )}
        </Flex>
      );
    },
    [shouldBlurField],
  );

  const renderSectionError = useCallback(
    (sectionId: string) => {
      if (!error || error.source !== sectionId) return null;

      return (
        <div className="AoASection__ErrorContainer">
          <ErrorCard variant="default" title="API error" error={error.message} onClose={() => setError(null)} />
        </div>
      );
    },
    [error],
  );

  const renderActionButton = useCallback(
    (sectionId: string, disabledReason: string | null) => {
      if (isReadOnly || DISABLED_ENRICH_BUTTON_SOURCES.includes(sectionId)) {
        return null;
      }

      const buttonLabel = AOA_SECTION_MAP[sectionId]?.buttonLabel || "Pull Data";
      const isDisabled = !!disabledReason;

      return (
        <Tooltip placement="top" tooltipRole="label">
          <TooltipTrigger style={{ width: "100%" }}>
            <AoaButton
              sovid={sovid ?? undefined}
              buttonRefs={buttonRefs}
              disabled={isDisabled}
              source={sectionId}
              buildingId={buildingId ?? undefined}
              triggerAoAQuery={triggerAoAQuery}
              isFetching={isSourceFetching(sectionId)}
              requestData={buildRequestData(sectionId)}
              integrationSettings={integrationSettings}
              label={buttonLabel}
              onFetchClicked={() => {
                onResetAoASection?.(sectionId);
                captureEvent(POSTHOG_EVENTS.ENRICH_BUTTON_CLICKED, {
                  source: sectionId,
                });
              }}
              onErrorChanged={(errorMsg: string) => {
                if (!ERROR_DISABLED_SECTIONS.includes(sectionId)) {
                  setError(errorMsg ? { source: sectionId, message: errorMsg } : null);
                }
              }}
              onSetAoAResponse={onSetAoAResponse}
            />
          </TooltipTrigger>
          {isDisabled && disabledReason && <TooltipContent>{disabledReason}</TooltipContent>}
        </Tooltip>
      );
    },
    [
      isReadOnly,
      sovid,
      buttonRefs,
      buildingId,
      triggerAoAQuery,
      isSourceFetching,
      buildRequestData,
      onResetAoASection,
      captureEvent,
      onSetAoAResponse,
      integrationSettings,
    ],
  );

  const renderSectionValues = useCallback(
    (sectionId: string) => {
      const rawIntegration = aoaIntegrations[sectionId];
      const hasRawData = rawIntegration && !Array.isArray(rawIntegration) && rawIntegration.fields;
      const sectionValues = hasRawData ? rawIntegration.fields : rawIntegration || [];
      const rawData = hasRawData ? rawIntegration.rawData : null;
      const shouldInsertLeadingSpacer = sectionId === SOURCES.GREEN_SHIELD_RESILIENCY_SCORE;

      return (
        <div className="AoASection__ValueSection">
          <div className="AoASection__ValueSection__Data">
            {sectionValues.map((value: any, index: number) => {
              const formattedValue = formatSectionTooltip(value);
              const fieldKey = `${sectionId}-${formattedValue.k}-${index}`;

              if (shouldInsertLeadingSpacer && index === 1) {
                return [
                  <div
                    key={`${sectionId}-leading-spacer`}
                    aria-hidden="true"
                    className="AoASection__ValueSection__Data__Spacer"
                  />,
                  renderTableField(sectionId, formattedValue, fieldKey),
                ];
              }

              return renderTableField(sectionId, formattedValue, fieldKey);
            })}
          </div>
          {rawData && (
            <button className="AoASection__ViewMoreButton" onClick={() => setJsonModalData(rawData)}>
              View More
            </button>
          )}
        </div>
      );
    },
    [aoaIntegrations, renderTableField],
  );

  const renderPropertyGuardianSplitSection = useCallback(
    (option: any, index: number) => {
      const propertyGuardianSplitSections = [
        {
          id: SOURCES.GREEN_SHIELD_WILDFIRE_RECURRENCE_SCORE,
          title: "Property Guardian-Wildfire Susceptibility Scores",
        },
        {
          id: SOURCES.GREEN_SHIELD_RESILIENCY_SCORE,
          title: "Resiliency Score",
        },
      ];

      return (
        <CollapsibleSection
          key={option.id}
          title={option.label}
          isInternationalLocationError={
            isInternationalLocationError && INTERNATIONAL_LOCATION_ERROR_SOURCES.includes(option.id)
          }
          initialOpen={index < INITIAL_AOA_SECTIONS_OPEN}
        >
          <div className="AoASection AoASection--Split">
            <div className="AoASection__SplitContainer">
              {propertyGuardianSplitSections.map((section) => {
                const disabledReason = getSectionDisabledReason(section.id);

                return (
                  <div key={section.id} className="AoASection__SplitContainer__Section">
                    <Flex
                      direction="row"
                      justify="space-between"
                      align="center"
                      gap="3"
                      className="AoASection__SplitContainer__Section__Header"
                    >
                      <span className="AoASection__SplitContainer__Section__Title">{section.title}</span>
                      <div className="AoASection__SplitContainer__Section__Button">
                        {renderActionButton(section.id, disabledReason)}
                      </div>
                    </Flex>
                    {renderSectionError(section.id)}
                    {renderSectionValues(section.id)}
                  </div>
                );
              })}
            </div>
          </div>
        </CollapsibleSection>
      );
    },
    [
      isInternationalLocationError,
      getSectionDisabledReason,
      renderActionButton,
      renderSectionError,
      renderSectionValues,
    ],
  );

  const renderSourceSection = useCallback(
    (option: any, index: number) => {
      const sectionId = option.id;
      const sectionState = sectionsState[sectionId];

      if (!sectionState) return null;

      if (sectionId === SOURCES.GREEN_SHIELD_ALL) {
        return renderPropertyGuardianSplitSection(option, index);
      }

      const fieldsToShow = getFieldsToShow(sectionId);
      const hasEditableFields = fieldsToShow.length > 0;
      const isE2ValueSection = sectionId === SOURCES.E2VALUE;
      const disabledReason = getSectionDisabledReason(sectionId);
      const sectionConfig = AOA_SECTION_MAP[sectionId];
      const sectionTitle = sectionConfig?.isBeta ? (
        <span>
          {option.label}
          <span className="AoASection__BetaBadge">Beta</span>
        </span>
      ) : (
        option.label
      );

      return (
        <CollapsibleSection
          key={sectionId}
          title={sectionTitle}
          isInternationalLocationError={
            isInternationalLocationError && INTERNATIONAL_LOCATION_ERROR_SOURCES.includes(sectionId)
          }
          initialOpen={index < INITIAL_AOA_SECTIONS_OPEN}
          titleButton={renderActionButton(sectionId, disabledReason)}
        >
          <div className="AoASection">
            {renderSectionError(sectionId)}

            {hasEditableFields && !isReadOnly && (
              <div className="AoASection__EditContainer">
                <div
                  className={cx("AoASection__EditContainer__Card", {
                    "AoASection__EditContainer__Card--NoFields": !hasEditableFields,
                  })}
                >
                  <Tooltip tooltipRole="label" placement="top">
                    <TooltipTrigger onClick={() => toggleEditing(sectionId)} style={{ width: "100%" }}>
                      <Flex
                        direction="row"
                        justify="space-between"
                        gap="2"
                        align="center"
                        className="AoASection__EditContainer__Card__EditButton__Content"
                      >
                        <span className="AoASection__EditContainer__Card__EditButton__Label">Additional Inputs</span>
                        <Icon iconName={sectionState.editing ? "ChevronUp" : "ChevronDown"} size={20} />
                      </Flex>
                    </TooltipTrigger>
                    <TooltipContent>{sectionState.editing ? "Close" : "Edit Values"}</TooltipContent>
                  </Tooltip>

                  {sectionState.editing && fieldsToShow.length > 0 && (
                    <>
                      <div
                        className={cx("AoASection__FieldsContainer", {
                          "AoASection__FieldsContainer--SingleColumn": isE2ValueSection,
                        })}
                      >
                        {fieldsToShow.map((field) => renderField(sectionId, field))}
                      </div>
                      <div className="AoASection__FieldsContainer__Button">
                        {renderActionButton(sectionId, disabledReason)}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {renderSectionValues(sectionId)}
          </div>
        </CollapsibleSection>
      );
    },
    [
      sectionsState,
      renderPropertyGuardianSplitSection,
      getFieldsToShow,
      getSectionDisabledReason,
      isInternationalLocationError,
      renderActionButton,
      renderSectionError,
      isReadOnly,
      toggleEditing,
      renderField,
      renderSectionValues,
    ],
  );

  return (
    <>
      {options && options.map(renderSourceSection)}
      <PermitsDetailModal isOpen={!!jsonModalData} onClose={() => setJsonModalData(null)} data={jsonModalData} />
    </>
  );
};
